import React from 'react';
import { motion } from 'framer-motion';
import { useMotionBudget } from '../../app/hooks/useMotionBudget';
interface StreakFlameProps {
  streak: number;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}
export function StreakFlame({
  streak,
  size = 'md',
  className = ''
}: StreakFlameProps) {
  const {
    shouldAnimate
  } = useMotionBudget();
  const sizeMap = {
    sm: 'w-8 h-12',
    md: 'w-12 h-16',
    lg: 'w-16 h-24'
  };
  const dimensions = sizeMap[size];
  // Determine flame level
  let level = 0;
  if (streak >= 1 && streak <= 2) level = 1;else if (streak >= 3 && streak <= 6) level = 2;else if (streak >= 7 && streak <= 13) level = 3;else if (streak >= 14 && streak <= 29) level = 4;else if (streak >= 30) level = 5;
  // Colors based on level
  const colors = {
    0: {
      outer: '#94a3b8',
      inner: '#cbd5e1',
      core: '#f1f5f9'
    },
    1: {
      outer: '#ea580c',
      inner: '#f97316',
      core: '#fdba74'
    },
    2: {
      outer: '#ea580c',
      inner: '#f59e0b',
      core: '#fde047'
    },
    3: {
      outer: '#dc2626',
      inner: '#ea580c',
      core: '#fef08a'
    },
    4: {
      outer: '#b91c1c',
      inner: '#ea580c',
      core: '#ffffff'
    },
    5: {
      outer: '#7e22ce',
      inner: '#d946ef',
      core: '#fde047'
    } // Legendary purple/gold
  };
  const currentColors = colors[level as keyof typeof colors];
  // Animation variants
  const breathe = shouldAnimate ? {
    scale: [1, 1.05, 1],
    transition: {
      duration: 2,
      repeat: Infinity,
      ease: 'easeInOut'
    }
  } : {};
  const flickerOuter = shouldAnimate && level > 0 ? {
    d: ['M 50 100 Q 20 70 30 40 Q 40 10 50 0 Q 60 10 70 40 Q 80 70 50 100', 'M 50 100 Q 15 75 35 45 Q 45 15 50 0 Q 55 15 65 45 Q 85 75 50 100', 'M 50 100 Q 25 65 25 35 Q 35 5 50 0 Q 65 5 75 35 Q 75 65 50 100', 'M 50 100 Q 20 70 30 40 Q 40 10 50 0 Q 60 10 70 40 Q 80 70 50 100'],
    transition: {
      duration: 0.8 - level * 0.1,
      repeat: Infinity,
      ease: 'easeInOut'
    }
  } : {
    d: 'M 50 100 Q 20 70 30 40 Q 40 10 50 0 Q 60 10 70 40 Q 80 70 50 100'
  };
  const flickerInner = shouldAnimate && level > 0 ? {
    d: ['M 50 95 Q 30 75 40 50 Q 45 30 50 20 Q 55 30 60 50 Q 70 75 50 95', 'M 50 95 Q 25 80 45 55 Q 50 35 50 20 Q 50 35 55 55 Q 75 80 50 95', 'M 50 95 Q 35 70 35 45 Q 40 25 50 20 Q 60 25 65 45 Q 65 70 50 95', 'M 50 95 Q 30 75 40 50 Q 45 30 50 20 Q 55 30 60 50 Q 70 75 50 95'],
    transition: {
      duration: 0.6 - level * 0.08,
      repeat: Infinity,
      ease: 'easeInOut',
      delay: 0.1
    }
  } : {
    d: 'M 50 95 Q 30 75 40 50 Q 45 30 50 20 Q 55 30 60 50 Q 70 75 50 95'
  };
  const flickerCore = shouldAnimate && level > 1 ? {
    d: ['M 50 90 Q 40 80 45 60 Q 48 45 50 40 Q 52 45 55 60 Q 60 80 50 90', 'M 50 90 Q 35 85 50 65 Q 53 50 50 40 Q 47 50 50 65 Q 65 85 50 90', 'M 50 90 Q 45 75 40 55 Q 45 40 50 40 Q 55 40 60 55 Q 55 75 50 90', 'M 50 90 Q 40 80 45 60 Q 48 45 50 40 Q 52 45 55 60 Q 60 80 50 90'],
    transition: {
      duration: 0.4 - level * 0.05,
      repeat: Infinity,
      ease: 'easeInOut',
      delay: 0.2
    }
  } : {
    d: 'M 50 90 Q 40 80 45 60 Q 48 45 50 40 Q 52 45 55 60 Q 60 80 50 90'
  };
  return <div className={`relative flex flex-col items-center justify-center ${className}`} data-id="element-2061">
      <motion.div className={`${dimensions} relative`} animate={breathe} data-id="element-2062">
        <svg viewBox="0 0 100 100" className="w-full h-full drop-shadow-md overflow-visible" data-id="element-2063">
          {/* Outer Flame */}
          <motion.path fill={currentColors.outer} animate={flickerOuter} style={{
          transformOrigin: '50% 100%'
        }} data-id="element-2064" />
          {/* Inner Flame */}
          {level > 0 && <motion.path fill={currentColors.inner} animate={flickerInner} style={{
          transformOrigin: '50% 100%'
        }} data-id="element-2065" />}
          {/* Core Flame */}
          {level > 1 && <motion.path fill={currentColors.core} animate={flickerCore} style={{
          transformOrigin: '50% 100%'
        }} data-id="element-2066" />}

          {/* Smoke wisps for level 0 */}
          {level === 0 && shouldAnimate && <>
              <motion.circle cx="40" cy="30" r="5" fill="#cbd5e1" opacity="0.5" animate={{
            y: [-10, -30],
            x: [0, -10],
            opacity: [0.5, 0],
            scale: [1, 2]
          }} transition={{
            duration: 2,
            repeat: Infinity,
            ease: 'easeOut'
          }} data-id="element-2067" />
              <motion.circle cx="60" cy="40" r="4" fill="#cbd5e1" opacity="0.5" animate={{
            y: [-10, -40],
            x: [0, 15],
            opacity: [0.5, 0],
            scale: [1, 2.5]
          }} transition={{
            duration: 2.5,
            repeat: Infinity,
            ease: 'easeOut',
            delay: 0.5
          }} data-id="element-2068" />
            </>}

          {/* Sparks for level 3+ */}
          {level >= 3 && shouldAnimate && <>
              <motion.circle cx="50" cy="50" r="2" fill="#fde047" animate={{
            y: [0, -50],
            x: [0, -20],
            opacity: [1, 0],
            scale: [1, 0]
          }} transition={{
            duration: 1,
            repeat: Infinity,
            ease: 'easeOut',
            delay: 0.2
          }} data-id="element-2069" />
              <motion.circle cx="50" cy="60" r="2" fill="#fde047" animate={{
            y: [0, -60],
            x: [0, 25],
            opacity: [1, 0],
            scale: [1, 0]
          }} transition={{
            duration: 1.2,
            repeat: Infinity,
            ease: 'easeOut',
            delay: 0.7
          }} data-id="element-2070" />
            </>}
          {level >= 4 && shouldAnimate && <motion.circle cx="50" cy="70" r="2.5" fill="#ffffff" animate={{
          y: [0, -70],
          x: [0, 10],
          opacity: [1, 0],
          scale: [1, 0]
        }} transition={{
          duration: 0.8,
          repeat: Infinity,
          ease: 'easeOut',
          delay: 0.4
        }} data-id="element-2071" />}
        </svg>

        {/* Streak number overlay */}
        <div className="absolute inset-0 flex items-end justify-center pb-1" data-id="element-2072">
          <span className={`font-black ${size === 'sm' ? 'text-xs' : size === 'md' ? 'text-sm' : 'text-lg'} ${level === 0 ? 'text-slate-500' : 'text-white'} drop-shadow-md`} data-id="element-2073">
            {streak}
          </span>
        </div>
      </motion.div>
    </div>;
}